
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { BedListing } from '@/types/listings';
import ListingFilters from './ListingFilters';
import SearchResults from './SearchResults';
import SavedSearches from './SavedSearches';
import HSSProviderCard from './HSSProviderCard';
import ListingDetailsModal from './ListingDetailsModal';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cacheService } from '@/services/CacheService';

const ListingSearch: React.FC = () => {
  const { toast } = useToast();
  const [listings, setListings] = useState<BedListing[]>([]);
  const [hssProviders, setHssProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'housing' | 'hss'>('housing');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCounties, setSelectedCounties] = useState<string[]>([]);
  const [selectedWaivers, setSelectedWaivers] = useState<string[]>([]);
  const [selectedAccessibility, setSelectedAccessibility] = useState<string[]>([]);
  const [selectedCareLevel, setSelectedCareLevel] = useState('all');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 5000]);
  const [bedsAvailable, setBedsAvailable] = useState(1);
  const [ageGroup, setAgeGroup] = useState('all');
  const [genderAccepted, setGenderAccepted] = useState('all');
  const [sortBy, setSortBy] = useState('last_updated');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [showSavedSearches, setShowSavedSearches] = useState(false);
  const [selectedListing, setSelectedListing] = useState<BedListing | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Debounced search to improve performance
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchListings();
    fetchHSSProviders();
    
    // Set up real-time listeners for bed_listings updates
    const channel = supabase
      .channel('bed-listings-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bed_listings'
        },
        (payload) => {
          console.log('New listing added:', payload.new);
          const newListing = payload.new as BedListing;
          
          setListings(prevListings => {
            // Add new listing and resort by last_updated
            const updatedListings = [newListing, ...prevListings];
            return updatedListings.sort((a, b) => 
              new Date(b.last_updated).getTime() - new Date(a.last_updated).getTime()
            );
          });
          
          toast({
            title: "New Listing Available",
            description: `${newListing.facility_name} has posted a new bed listing.`,
            duration: 5000,
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bed_listings'
        },
        (payload) => {
          console.log('Listing updated:', payload.new);
          const updatedListing = payload.new as BedListing;
          
          setListings(prevListings => {
            const updatedListings = prevListings.map(listing =>
              listing.id === updatedListing.id ? updatedListing : listing
            );
            
            // Resort by last_updated to reflect the update
            return updatedListings.sort((a, b) => 
              new Date(b.last_updated).getTime() - new Date(a.last_updated).getTime()
            );
          });
          
          toast({
            title: "Listing Updated",
            description: `${updatedListing.facility_name} has updated their listing.`,
            duration: 3000,
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'bed_listings'
        },
        (payload) => {
          console.log('Listing deleted:', payload.old);
          const deletedListing = payload.old as BedListing;
          
          setListings(prevListings => 
            prevListings.filter(listing => listing.id !== deletedListing.id)
          );
          
          toast({
            title: "Listing Removed",
            description: `A bed listing has been removed.`,
            duration: 3000,
          });
        }
      )
      .subscribe();

    // Cleanup subscription on component unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, [toast]);

  const fetchListings = async () => {
    console.log('Fetching listings...');
    setLoading(true);
    
    // Try to get from cache first
    const cacheKey = 'listings';
    const cachedListings = cacheService.get<BedListing[]>(cacheKey);
    
    if (cachedListings && cachedListings.length > 0) {
      console.log('Using cached listings:', cachedListings.length);
      setListings(cachedListings);
      setLoading(false);
      return;
    }
    
    try {
      // Check if user can view contact details using our security function
      const { data: canViewContact } = await supabase.rpc('can_view_contact_details');
      
      let selectFields = `
        id, facility_name, care_level, waiver_types, gender_accepted, age_group,
        location, county, description, amenities, accessibility, rent_amount,
        beds_available, total_beds, clicks, last_updated, created_at,
        listing_type, service_type, licensed_capacity, scorecard_id, created_by
      `;
      
      // Only include contact fields if user has permission
      if (canViewContact) {
        selectFields += ', contact_person, contact_phone, contact_email';
      }

      const { data, error } = await supabase
        .from('bed_listings')
        .select(selectFields)
        .gt('beds_available', 0)
        .order('last_updated', { ascending: false });

      console.log('Listings query result:', { data, error });
      if (error) throw error;
      console.log('Setting listings:', data);
      
      // Type guard to ensure data is valid and properly typed
      if (data && Array.isArray(data)) {
        // Convert to unknown first to handle the generic string error type
        const unknownData = data as unknown;
        if (Array.isArray(unknownData) && (unknownData.length === 0 || 
            (unknownData.length > 0 && typeof unknownData[0] === 'object' && unknownData[0] !== null && 'id' in unknownData[0]))) {
          const typedListings = unknownData as BedListing[];
          setListings(typedListings);
          // Cache the listings for 5 minutes
          cacheService.set(cacheKey, typedListings, 5 * 60 * 1000);
        } else {
          setListings([]);
        }
      } else {
        setListings([]);
      }
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHSSProviders = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'hss_provider')
        .order('name', { ascending: true });

      if (error) throw error;
      setHssProviders(data || []);
    } catch (error) {
      console.error('Error fetching HSS providers:', error);
    }
  };

  // Advanced filtering with improved performance
  const filteredAndSortedListings = useMemo(() => {
    console.log('Processing listings:', listings.length, 'listings');
    let filtered = listings;

    // Search term filter
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(listing =>
        listing.facility_name.toLowerCase().includes(searchLower) ||
        listing.location.toLowerCase().includes(searchLower) ||
        listing.care_level.toLowerCase().includes(searchLower) ||
        listing.county.toLowerCase().includes(searchLower) ||
        listing.description?.toLowerCase().includes(searchLower) ||
        listing.amenities.some(amenity => amenity.toLowerCase().includes(searchLower))
      );
    }

    // County filter
    if (selectedCounties.length > 0) {
      filtered = filtered.filter(listing =>
        selectedCounties.includes(listing.county)
      );
    }

    // Waiver types filter
    if (selectedWaivers.length > 0) {
      filtered = filtered.filter(listing =>
        selectedWaivers.some(waiver => listing.waiver_types.includes(waiver))
      );
    }

    // Accessibility filter
    if (selectedAccessibility.length > 0) {
      filtered = filtered.filter(listing =>
        selectedAccessibility.some(feature => listing.accessibility?.includes(feature))
      );
    }

    // Care level filter
    if (selectedCareLevel && selectedCareLevel !== 'all') {
      filtered = filtered.filter(listing => listing.care_level === selectedCareLevel);
    }

    // Price range filter
    filtered = filtered.filter(listing =>
      listing.rent_amount >= priceRange[0] && listing.rent_amount <= priceRange[1]
    );

    // Beds available filter
    filtered = filtered.filter(listing =>
      listing.beds_available >= bedsAvailable
    );

    // Age group filter
    if (ageGroup && ageGroup !== 'all') {
      filtered = filtered.filter(listing => listing.age_group === ageGroup);
    }

    // Gender accepted filter
    if (genderAccepted && genderAccepted !== 'all') {
      filtered = filtered.filter(listing =>
        listing.gender_accepted.includes(genderAccepted) || 
        listing.gender_accepted.includes('Both')
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rent_asc':
          return a.rent_amount - b.rent_amount;
        case 'rent_desc':
          return b.rent_amount - a.rent_amount;
        case 'beds_desc':
          return b.beds_available - a.beds_available;
        case 'facility_name':
          return a.facility_name.localeCompare(b.facility_name);
        case 'last_updated':
        default:
          return new Date(b.last_updated).getTime() - new Date(a.last_updated).getTime();
      }
    });
    
    console.log('Final filtered results:', filtered.length, 'listings');
    console.log('Returning filtered listings:', filtered);
    return filtered;
  }, [
    listings,
    debouncedSearchTerm,
    selectedCounties,
    selectedWaivers,
    selectedAccessibility,
    selectedCareLevel,
    priceRange,
    bedsAvailable,
    ageGroup,
    genderAccepted,
    sortBy
  ]);

  const handleCountyChange = (county: string, checked: boolean) => {
    setSelectedCounties(prev =>
      checked ? [...prev, county] : prev.filter(c => c !== county)
    );
  };

  const handleWaiverChange = (waiver: string, checked: boolean) => {
    setSelectedWaivers(prev =>
      checked ? [...prev, waiver] : prev.filter(w => w !== waiver)
    );
  };

  const handleAccessibilityChange = (accessibility: string, checked: boolean) => {
    setSelectedAccessibility(prev =>
      checked ? [...prev, accessibility] : prev.filter(a => a !== accessibility)
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCounties([]);
    setSelectedWaivers([]);
    setSelectedAccessibility([]);
    setSelectedCareLevel('all');
    setPriceRange([0, 1000]);
    setBedsAvailable(1);
    setAgeGroup('all');
    setGenderAccepted('all');
  };

  const handleListingClick = async (listing: BedListing) => {
    try {
      // Track the click for analytics
      const { error } = await supabase.rpc('track_listing_click', {
        listing_id: listing.id
      });

      if (error) {
        console.error('Error tracking click:', error);
      }

      // Show listing details
      setSelectedListing(listing);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error handling listing click:', error);
    }
  };

  const getCurrentCriteria = () => ({
    searchTerm,
    selectedCounties,
    selectedWaivers,
    selectedAccessibility,
    selectedCareLevel,
    priceRange,
    bedsAvailable,
    ageGroup,
    genderAccepted,
  });

  const loadSavedSearch = (criteria: any) => {
    setSearchTerm(criteria.searchTerm || '');
    setSelectedCounties(criteria.selectedCounties || []);
    setSelectedWaivers(criteria.selectedWaivers || []);
    setSelectedAccessibility(criteria.selectedAccessibility || []);
    setSelectedCareLevel(criteria.selectedCareLevel || 'all');
    setPriceRange(criteria.priceRange || [0, 1000]);
    setBedsAvailable(criteria.bedsAvailable || 1);
    setAgeGroup(criteria.ageGroup || 'all');
    setGenderAccepted(criteria.genderAccepted || 'all');
    setShowSavedSearches(false);
  };

  const hasActiveFilters = selectedCounties.length > 0 || selectedWaivers.length > 0 || 
    selectedAccessibility.length > 0 || (selectedCareLevel && selectedCareLevel !== 'all') || 
    priceRange[0] > 0 || priceRange[1] < 1000 || bedsAvailable > 1 || 
    (ageGroup && ageGroup !== 'all') || (genderAccepted && genderAccepted !== 'all');

  const handleHSSProviderContact = (provider: any) => {
    const contactMethod = provider.preferred_contact_method === 'phone' && provider.phone
      ? `tel:${provider.phone}`
      : `mailto:${provider.email}`;
    
    window.open(contactMethod, '_blank');
    
    toast({
      title: 'Contact Information',
      description: `Opening ${provider.preferred_contact_method === 'phone' ? 'phone' : 'email'} for ${provider.agency_name || provider.name}`,
    });
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'housing' | 'hss')} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="housing">Housing Placement</TabsTrigger>
          <TabsTrigger value="hss">HSS Services</TabsTrigger>
        </TabsList>

        <TabsContent value="housing">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar - Filters and Saved Searches */}
            <div className="lg:col-span-1 space-y-4">
              {/* Search Input */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Search Housing</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search facilities, locations, amenities..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex justify-between items-center mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                      className="flex items-center gap-2"
                    >
                      <SlidersHorizontal className="h-4 w-4" />
                      Filters
                      {hasActiveFilters && (
                        <Badge variant="secondary" className="ml-1">
                          {selectedCounties.length + selectedWaivers.length + selectedAccessibility.length + 
                           (selectedCareLevel && selectedCareLevel !== 'all' ? 1 : 0) + (bedsAvailable > 1 ? 1 : 0) + 
                           (ageGroup && ageGroup !== 'all' ? 1 : 0) + (genderAccepted && genderAccepted !== 'all' ? 1 : 0)}
                        </Badge>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSavedSearches(!showSavedSearches)}
                    >
                      Saved
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Filters */}
              {showFilters && (
                <ListingFilters
                  selectedCounties={selectedCounties}
                  selectedWaivers={selectedWaivers}
                  selectedAccessibility={selectedAccessibility}
                  selectedCareLevel={selectedCareLevel}
                  priceRange={priceRange}
                  bedsAvailable={bedsAvailable}
                  ageGroup={ageGroup}
                  genderAccepted={genderAccepted}
                  onCountyChange={handleCountyChange}
                  onWaiverChange={handleWaiverChange}
                  onAccessibilityChange={handleAccessibilityChange}
                  onCareLevelChange={setSelectedCareLevel}
                  onPriceRangeChange={setPriceRange}
                  onBedsAvailableChange={setBedsAvailable}
                  onAgeGroupChange={setAgeGroup}
                  onGenderAcceptedChange={setGenderAccepted}
                  onClearFilters={clearFilters}
                />
              )}

              {/* Saved Searches */}
              {showSavedSearches && (
                <SavedSearches
                  currentCriteria={getCurrentCriteria()}
                  onLoadSearch={loadSavedSearch}
                />
              )}
            </div>

            {/* Main Content - Search Results */}
            <div className="lg:col-span-3">
              <SearchResults
                listings={filteredAndSortedListings}
                loading={loading}
                sortBy={sortBy}
                viewMode={viewMode}
                onSortChange={setSortBy}
                onViewModeChange={setViewMode}
                onListingClick={handleListingClick}
                searchTerm={debouncedSearchTerm}
                totalResults={filteredAndSortedListings.length}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="hss">
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">Housing Support Services (HSS) Providers</h2>
              <p className="text-muted-foreground">
                Find HSS agencies currently accepting referrals for housing stabilization services
              </p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {hssProviders.length === 0 ? (
                  <div className="col-span-full text-center py-8">
                    <p className="text-muted-foreground">No HSS providers found</p>
                  </div>
                ) : (
                  hssProviders.map((provider) => (
                    <HSSProviderCard
                      key={provider.id}
                      provider={provider}
                      onContact={handleHSSProviderContact}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <ListingDetailsModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        listing={selectedListing!}
      />
    </div>
  );
};

export default ListingSearch;
